import {
  createContext,
  useContext,
  useEffect,
  useState,
  ReactNode,
} from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '../lib/supabase/client';
import type { Profile } from '../types';

interface AuthContextType {
  user: User | null;
  profile: Profile | null;
  session: Session | null;
  isLoading: boolean;
  isAdmin: boolean;
  signIn: (email: string, password: string) => Promise<{ error: Error | null }>;
  signInWithMagicLink: (email: string) => Promise<{ error: Error | null }>;
  resetPassword: (email: string) => Promise<{ error: Error | null }>;
  signOut: () => Promise<void>;
  refreshProfile: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [initialLoadDone, setInitialLoadDone] = useState(false);

  // Cache admin status to prevent flickering on refresh
  const [isAdmin, setIsAdmin] = useState<boolean>(() => {
    const cached = localStorage.getItem('churnlogix-is-admin');
    return cached === 'true';
  });

  const fetchProfile = async (userId: string, retries = 3): Promise<Profile | null> => {
    console.log('Fetching profile for user:', userId);

    for (let i = 0; i < retries; i++) {
      try {
        const { data, error } = await supabase
          .from('profiles')
          .select('*')
          .eq('id', userId)
          .single();

        if (error) {
          // If it's an abort error, retry
          if (error.message?.includes('AbortError') || error.message?.includes('aborted')) {
            console.log('Request aborted, retrying...', i + 1);
            await new Promise(resolve => setTimeout(resolve, 100 * (i + 1)));
            continue;
          }
          console.error('Error fetching profile:', error);
          return null;
        }

        console.log('Profile fetched:', data);
        return data as Profile;
      } catch (err) {
        console.error('Exception fetching profile:', err);
        if (i < retries - 1) {
          await new Promise(resolve => setTimeout(resolve, 100 * (i + 1)));
          continue;
        }
      }
    }

    return null;
  };

  const refreshProfile = async () => {
    if (user) {
      const profileData = await fetchProfile(user.id);
      setProfile(profileData);
    }
  };

  useEffect(() => {
    // Get initial session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      // Stop loading immediately - session check is fast (from localStorage)
      setIsLoading(false);
      setInitialLoadDone(true);

      // Fetch profile in background (don't block rendering)
      if (session?.user) {
        fetchProfile(session.user.id).then((profileData) => {
          setProfile(profileData);
          const adminStatus = profileData?.role === 'admin';
          setIsAdmin(adminStatus);
          localStorage.setItem('churnlogix-is-admin', String(adminStatus));
        });
      } else {
        // Clear admin status if no session
        setIsAdmin(false);
        localStorage.removeItem('churnlogix-is-admin');
      }
    });

    // Listen for auth changes (but skip the initial event since getSession handles it)
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (event, session) => {
      // Skip INITIAL_SESSION event - we already handle this in getSession
      if (event === 'INITIAL_SESSION') {
        return;
      }

      setSession(session);
      setUser(session?.user ?? null);

      if (session?.user) {
        const profileData = await fetchProfile(session.user.id);
        setProfile(profileData);
        const adminStatus = profileData?.role === 'admin';
        setIsAdmin(adminStatus);
        localStorage.setItem('churnlogix-is-admin', String(adminStatus));
      } else {
        setProfile(null);
        setIsAdmin(false);
        localStorage.removeItem('churnlogix-is-admin');
      }

      setIsLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    return { error: error as Error | null };
  };

  const signInWithMagicLink = async (email: string) => {
    const { error } = await supabase.auth.signInWithOtp({
      email,
      options: {
        emailRedirectTo: `${window.location.origin}/dashboard`,
      },
    });
    return { error: error as Error | null };
  };

  const resetPassword = async (email: string) => {
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/dashboard`,
    });
    return { error: error as Error | null };
  };

  const signOut = async () => {
    await supabase.auth.signOut();
    setUser(null);
    setProfile(null);
    setSession(null);
    setIsAdmin(false);
    localStorage.removeItem('churnlogix-is-admin');
  };

  const value = {
    user,
    profile,
    session,
    isLoading,
    isAdmin,
    signIn,
    signInWithMagicLink,
    resetPassword,
    signOut,
    refreshProfile,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
